import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    ArrowLeft, Users, Globe, Mail, Phone, User,
    MessageSquare, Calendar, FileText, Bot, Send,
    Edit3, Save, X, Check, RefreshCw, ExternalLink, Paperclip,
    Plus, Trash2, Clock, Video,
    Download, Image as ImageIcon,
    Zap, Briefcase, ChevronLeft, ChevronRight, Eye, Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const STATUS_OPTIONS = [
    { value: 'contract_signed', label: '🟢 კონტრაქტი გაფორმდა (მწვანე)', category: 'green', color: 'success', border: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)', text: '#15803d' },
    { value: 'meeting_scheduled', label: '🟡 შეხვედრა დაგეგმილია (ყვითელი)', category: 'yellow', color: 'warning', border: '#eab308', bg: 'rgba(234, 179, 8, 0.2)', text: '#854d0e' },
    { value: 'negotiation', label: '🟡 მოლაპარაკება (ყვითელი)', category: 'yellow', color: 'warning', border: '#eab308', bg: 'rgba(234, 179, 8, 0.2)', text: '#854d0e' },
    { value: 'offer_sent', label: '🔵 შეთავაზება გაგზავნილია (ლურჯი)', category: 'blue', color: 'info', border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)', text: '#1e40af' },
    { value: 'lead', label: '🔴 ლიდი / დასაწყისი (წითელი)', category: 'red', color: 'danger', border: '#f87171', bg: 'rgba(239, 68, 68, 0.15)', text: '#b91c1c' },
    { value: 'rejected', label: '🔴 უარყოფილია (წითელი)', category: 'red', color: 'danger', border: '#f87171', bg: 'rgba(239, 68, 68, 0.15)', text: '#b91c1c' },
];

const SECTORS = [
    { value: 'business', label: '🏢 კერძო ბიზნესი / Enterprise' },
    { value: 'gov', label: '🏛️ საჯარო / სახელმწიფო' },
    { value: 'education', label: '🎓 განათლება & უნივერსიტეტი' },
    { value: 'healthcare', label: '🏥 მედიცინა & ჯანდაცვა' },
    { value: 'fintech', label: '💳 ფინტექი & ბანკინგი' },
    { value: 'ngo', label: '🌐 არასამთავრობო / ფონდი' },
    { value: 'other', label: '📦 სხვა სფერო' },
];

const EVENT_TYPES = [
    { value: 'meeting', label: 'ონლაინ შეხვედრა', icon: <Video size={14} /> },
    { value: 'follow_up', label: 'Follow-up', icon: <Phone size={14} /> },
    { value: 'offer_deadline', label: 'შეთავაზების ვადა', icon: <Clock size={14} /> },
    { value: 'demo', label: 'დემო პრეზენტაცია', icon: <Briefcase size={14} /> },
    { value: 'milestone', label: 'მაილსთოუნი', icon: <Check size={14} /> },
];

const AdminPotentialCustomerDetail = () => {
    const { id } = useParams();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isKa = i18n.language === 'ka';
    const chatEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('chat');
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [chatMessage, setChatMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isGeneratingOffer, setIsGeneratingOffer] = useState(false);
    const [allCustomers, setAllCustomers] = useState([]);
    const [prevHover, setPrevHover] = useState(false);
    const [nextHover, setNextHover] = useState(false);

    // Event form
    const [showEventForm, setShowEventForm] = useState(false);
    const [eventForm, setEventForm] = useState({
        title: '', event_date: '', event_type: 'meeting',
        meeting_link: '', description: ''
    });

    // Contact form
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactForm, setContactForm] = useState({
        first_name: '', last_name: '', position: '',
        phone: '', email: '', notes: ''
    });

    // Lightbox & PDF modal state
    const [activePdfModal, setActivePdfModal] = useState(null);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    const openLightbox = (imgs, idx = 0) => {
        setLightboxImages(imgs);
        setLightboxIndex(idx);
        setIsLightboxOpen(true);
    };

    const closeLightbox = () => setIsLightboxOpen(false);

    const prevLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === 0 ? lightboxImages.length - 1 : prev - 1));
    }, [lightboxImages.length]);

    const nextLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === lightboxImages.length - 1 ? 0 : prev + 1));
    }, [lightboxImages.length]);

    useEffect(() => {
        if (!isLightboxOpen && !activePdfModal) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isLightboxOpen) closeLightbox();
                if (activePdfModal) setActivePdfModal(null);
            }
            if (isLightboxOpen) {
                if (e.key === 'ArrowLeft') prevLightboxImage();
                if (e.key === 'ArrowRight') nextLightboxImage();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLightboxOpen, activePdfModal, prevLightboxImage, nextLightboxImage]);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php?id=${id}`);
            const data = await res.json();
            if (data && !data.error) {
                setCustomer(data);
                setEditForm({
                    name: data.name || '',
                    sector: data.sector || 'business',
                    status: data.status || 'lead',
                    priority: data.priority || 'medium',
                    contact_person: data.contact_person || '',
                    contact_position: data.contact_position || '',
                    contact_email: data.contact_email || '',
                    contact_phone: data.contact_phone || '',
                    website: data.website || '',
                    client_wants: data.client_wants || '',
                    our_offer: data.our_offer || '',
                    offer_type: data.offer_type || 'custom_solution',
                    estimated_budget: data.estimated_budget || 0,
                    meeting_link: data.meeting_link || '',
                    notes: data.notes || '',
                    autonomous_mode: data.autonomous_mode ?? 1,
                });
            } else {
                toast.error(isKa ? 'კლიენტი ვერ მოიძებნა' : 'Customer not found');
                navigate('/admin/potential-customers');
            }
        } catch (err) {
            toast.error(isKa ? 'ჩატვირთვა ვერ მოხერხდა' : 'Failed to load');
        }
        setLoading(false);
    }, [id, navigate, isKa]);

    useEffect(() => {
        if (user) fetchData();
    }, [user, fetchData]);

    // Fetch all for Prev/Next
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const res = await fetchWithSecurity(`${API_URL}customers.php`);
                const data = await res.json();
                if (Array.isArray(data)) {
                    setAllCustomers(data.map(c => ({ id: c.id, name: c.name, sector: c.sector })));
                }
            } catch {}
        };
        if (user) fetchAll();
    }, [user]);

    useEffect(() => {
        if (chatEndRef.current && activeTab === 'chat') {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [customer?.communications, activeTab]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: parseInt(id), ...editForm })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'ცვლილებები შენახულია!' : 'Changes saved!');
                setEditing(false);
                fetchData();
            } else {
                toast.error(data.error || (isKa ? 'შენახვა ვერ მოხერხდა' : 'Failed to save'));
            }
        } catch (err) {
            toast.error(isKa ? 'შენახვა ვერ მოხერხდა' : 'Failed to save');
        }
        setSaving(false);
    };

    const handleSendMessage = async () => {
        if (!chatMessage.trim()) return;
        setIsSending(true);
        const msg = chatMessage.trim();
        setChatMessage('');
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_communication',
                    customer_id: parseInt(id),
                    sender_type: 'agency',
                    sender_name: user?.username || 'Orbitronix Agency',
                    channel: 'portal_chat',
                    message: msg
                })
            });
            const data = await res.json();
            if (data.success) {
                fetchData();
            } else {
                toast.error(isKa ? 'შეტყობინება ვერ გაიგზავნა' : 'Failed to send message');
            }
        } catch (err) {
            toast.error(isKa ? 'შეტყობინება ვერ გაიგზავნა' : 'Failed to send message');
        }
        setIsSending(false);
    };

    const handleGenerateOffer = async () => {
        setIsGeneratingOffer(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'generate_autonomous_offer',
                    customer_id: parseInt(id),
                    client_wants: editForm.client_wants,
                    our_offer: editForm.our_offer,
                    estimated_budget: editForm.estimated_budget
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'ოფიციალური შეთავაზება წარმატებით დაგენერირდა!' : 'Autonomous proposal generated!');
                fetchData();
                setActiveTab('chat');
            } else {
                toast.error(data.error || (isKa ? 'გენერირება ვერ მოხერხდა' : 'Failed to generate proposal'));
            }
        } catch (err) {
            toast.error(isKa ? 'გენერირება ვერ მოხერხდა' : 'Failed to generate proposal');
        }
        setIsGeneratingOffer(false);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        try {
            toast.info(isKa ? 'ფაილი იტვირთება...' : 'Uploading file...');
            const upRes = await fetchWithSecurity(`${API_URL}upload.php`, {
                method: 'POST',
                body: formData
            });
            const upData = await upRes.json();
            if (upData.success && upData.url) {
                const ext = file.name.split('.').pop().toLowerCase();
                const fileType = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(ext) ? 'image' : (ext === 'pdf' ? 'pdf' : 'document');

                await fetchWithSecurity(`${API_URL}customers.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'attach_file',
                        customer_id: parseInt(id),
                        file_name: file.name,
                        file_url: upData.url,
                        file_type: fileType,
                        file_size: file.size,
                        uploaded_by: user?.username || 'Staff'
                    })
                });
                toast.success(isKa ? 'ფაილი წარმატებით აიტვირთა!' : 'File uploaded!');
                fetchData();
            } else {
                toast.error(upData.error || (isKa ? 'ატვირთვა ვერ მოხერხდა' : 'Upload failed'));
            }
        } catch (err) {
            toast.error(isKa ? 'ატვირთვა ვერ მოხერხდა' : 'Upload failed');
        }
        e.target.value = '';
    };

    const handleAddContact = async (e) => {
        e.preventDefault();
        if (!contactForm.first_name.trim()) {
            toast.error(isKa ? 'სახელი სავალდებულოა' : 'Name is required');
            return;
        }
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_contact',
                    customer_id: parseInt(id),
                    ...contactForm
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'საკონტაქტო პირი დაემატა!' : 'Contact added!');
                setShowContactForm(false);
                setContactForm({ first_name: '', last_name: '', position: '', phone: '', email: '', notes: '' });
                fetchData();
            } else {
                toast.error(data.error || (isKa ? 'ვერ დაემატა' : 'Failed to add'));
            }
        } catch (err) {
            toast.error(isKa ? 'ვერ დაემატა' : 'Failed to add');
        }
    };

    const handleDeleteContact = async (contactId) => {
        if (!window.confirm(isKa ? 'დარწმუნებული ხართ, რომ გსურთ ამ კონტაქტის წაშლა?' : 'Delete contact?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_contact',
                    contact_id: contactId
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'კონტაქტი წაიშალა' : 'Contact deleted');
                fetchData();
            } else {
                toast.error(data.error || (isKa ? 'წაშლა ვერ მოხერხდა' : 'Failed'));
            }
        } catch (err) {
            toast.error(isKa ? 'წაშლა ვერ მოხერხდა' : 'Failed');
        }
    };

    const handleAddEvent = async () => {
        if (!eventForm.title || !eventForm.event_date) {
            toast.error(isKa ? 'სახელი და თარიღი სავალდებულოა' : 'Title and date are required');
            return;
        }
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add_event',
                    customer_id: parseInt(id),
                    ...eventForm
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'ღონისძიება დაემატა!' : 'Event scheduled!');
                setShowEventForm(false);
                setEventForm({ title: '', event_date: '', event_type: 'meeting', meeting_link: '', description: '' });
                fetchData();
            } else {
                toast.error(data.error || (isKa ? 'ვერ დაემატა' : 'Failed'));
            }
        } catch (err) {
            toast.error(isKa ? 'ვერ დაემატა' : 'Failed');
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm(isKa ? 'წავშალოთ ღონისძიება?' : 'Delete event?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete_event', event_id: eventId })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'ღონისძიება წაიშალა' : 'Event deleted');
                fetchData();
            }
        } catch {}
    };

    if (authLoading || !user) return null;

    const statusCfg = STATUS_OPTIONS.find(s => s.value === customer?.status) || STATUS_OPTIONS[0];
    const communications = customer?.communications || [];
    const files = customer?.files || [];
    const events = customer?.events || [];
    const contacts = customer?.contacts || [];

    const TABS = [
        { key: 'chat', label: isKa ? 'ჩატი & კომუნიკაცია' : 'Communications', icon: <MessageSquare size={16} />, count: communications.length },
        { key: 'contacts', label: isKa ? 'საკონტაქტო პირები' : 'Contacts', icon: <Users size={16} />, count: contacts.length },
        { key: 'calendar', label: isKa ? 'კალენდარი' : 'Calendar', icon: <Calendar size={16} />, count: events.filter(e => e.status === 'scheduled').length },
        { key: 'files', label: isKa ? 'ფაილები & შეთავაზებები' : 'Files & Proposals', icon: <FileText size={16} />, count: files.length },
        { key: 'details', label: isKa ? 'დეტალები' : 'Details', icon: <Edit3 size={16} />, count: null },
    ];

    return (
        <AdminLayout title={customer?.name || (isKa ? 'პოტენციური კლიენტი' : 'Potential Customer')}>
            {/* Top Bar: Back & Prev/Next & Status */}
            <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
                <button
                    className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                    onClick={() => navigate('/admin/potential-customers')}
                >
                    <ArrowLeft size={16} /> {isKa ? 'პოტენციური კლიენტები' : 'Back to Customers'}
                </button>

                {/* Prev/Next Navigation */}
                {allCustomers.length > 1 && (() => {
                    const currentIndex = allCustomers.findIndex(c => String(c.id) === String(id));
                    const prevCust = currentIndex > 0 ? allCustomers[currentIndex - 1] : null;
                    const nextCust = currentIndex < allCustomers.length - 1 ? allCustomers[currentIndex + 1] : null;
                    return (
                        <div className="d-flex gap-1">
                            {/* Prev */}
                            <div className="position-relative">
                                <button
                                    className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                    disabled={!prevCust}
                                    onClick={() => prevCust && navigate(`/admin/potential-customers/${prevCust.id}`)}
                                    onMouseEnter={() => setPrevHover(true)}
                                    onMouseLeave={() => setPrevHover(false)}
                                    title={prevCust?.name || ''}
                                    style={{ opacity: prevCust ? 1 : 0.4 }}
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                {prevHover && prevCust && (
                                    <div style={{
                                        position: 'absolute', top: '110%', left: 0, zIndex: 500,
                                        background: '#fff', border: '1px solid #e2e8f0',
                                        borderRadius: '12px', padding: '10px 14px', minWidth: '170px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                                    }}>
                                        <div className="smallest text-muted">{isKa ? 'წინა კლიენტი:' : 'Previous:'}</div>
                                        <span className="fw-semibold text-dark small">{prevCust.name}</span>
                                    </div>
                                )}
                            </div>
                            {/* Next */}
                            <div className="position-relative">
                                <button
                                    className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                    disabled={!nextCust}
                                    onClick={() => nextCust && navigate(`/admin/potential-customers/${nextCust.id}`)}
                                    onMouseEnter={() => setNextHover(true)}
                                    onMouseLeave={() => setNextHover(false)}
                                    title={nextCust?.name || ''}
                                    style={{ opacity: nextCust ? 1 : 0.4 }}
                                >
                                    <ChevronRight size={16} />
                                </button>
                                {nextHover && nextCust && (
                                    <div style={{
                                        position: 'absolute', top: '110%', right: 0, zIndex: 500,
                                        background: '#fff', border: '1px solid #e2e8f0',
                                        borderRadius: '12px', padding: '10px 14px', minWidth: '170px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                                    }}>
                                        <div className="smallest text-muted">{isKa ? 'შემდეგი კლიენტი:' : 'Next:'}</div>
                                        <span className="fw-semibold text-dark small">{nextCust.name}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {customer && (
                    <span 
                        className="badge rounded-pill px-3 py-1 fs-7 fw-semibold"
                        style={{
                            background: statusCfg.bg,
                            color: statusCfg.text,
                            border: `1.5px solid ${statusCfg.border}`
                        }}
                    >
                        {statusCfg.label}
                    </span>
                )}

                {customer?.autonomous_mode === 1 && (
                    <span className="shadcn-badge shadcn-badge-info d-flex align-items-center gap-1">
                        <Bot size={12} /> AI Lead Automation
                    </span>
                )}

                <div className="ms-auto d-flex gap-2">
                    <button
                        className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                        onClick={fetchData}
                    >
                        <RefreshCw size={14} /> {isKa ? 'განახლება' : 'Refresh'}
                    </button>
                    {!editing ? (
                        <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={() => setEditing(true)}>
                            <Edit3 size={14} /> {isKa ? 'რედაქტირება' : 'Edit'}
                        </button>
                    ) : (
                        <div className="d-flex gap-1">
                            <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => setEditing(false)}>
                                <X size={14} /> {isKa ? 'გაუქმება' : 'Cancel'}
                            </button>
                            <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={handleSave} disabled={saving}>
                                <Save size={14} /> {saving ? (isKa ? 'ინახება...' : 'Saving...') : (isKa ? 'შენახვა' : 'Save')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5 text-muted">იტვირთება...</div>
            ) : !customer ? null : (
                <div className="row g-4">
                    {/* ── LEFT COLUMN (4 COLS) ── */}
                    <div className="col-lg-4">
                        {/* Profile Info Card */}
                        <div className="shadcn-card p-4 mb-3">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <div 
                                    className="rounded-3 d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary fw-bold fs-4"
                                    style={{ width: '56px', height: '56px' }}
                                >
                                    <Building2 size={30} />
                                </div>
                                <div className="text-truncate">
                                    <h5 className="fw-bold text-dark mb-0 text-truncate">{customer.name}</h5>
                                    <span className="smallest text-muted text-uppercase fw-semibold">
                                        {SECTORS.find(s => s.value === customer.sector)?.label || customer.sector}
                                    </span>
                                </div>
                            </div>

                            {/* Status changer in edit mode */}
                            {editing ? (
                                <div className="mb-3 p-3 bg-light rounded-3">
                                    <label className="form-label smallest fw-bold mb-1">{isKa ? 'სტატუსი' : 'Status'}</label>
                                    <select
                                        className="shadcn-input mb-2"
                                        value={editForm.status}
                                        onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                                    >
                                        {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                    <label className="form-label smallest fw-bold mb-1">{isKa ? 'სექტორი' : 'Sector'}</label>
                                    <select
                                        className="shadcn-input mb-2"
                                        value={editForm.sector}
                                        onChange={e => setEditForm({ ...editForm, sector: e.target.value })}
                                    >
                                        {SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                    <label className="form-label smallest fw-bold mb-1">{isKa ? 'პრიორიტეტი' : 'Priority'}</label>
                                    <select
                                        className="shadcn-input"
                                        value={editForm.priority}
                                        onChange={e => setEditForm({ ...editForm, priority: e.target.value })}
                                    >
                                        <option value="low">დაბალი (Low)</option>
                                        <option value="medium">საშუალო (Medium)</option>
                                        <option value="high">მაღალი (High)</option>
                                        <option value="urgent">გადაუდებელი (Urgent)</option>
                                    </select>
                                </div>
                            ) : null}

                            {/* Contact Details List */}
                            <div className="d-flex flex-column gap-2 border-top pt-3">
                                {customer.contact_person && (
                                    <div className="d-flex align-items-center gap-2 small text-dark">
                                        <User size={14} className="text-primary flex-shrink-0" />
                                        <span className="fw-semibold">{customer.contact_person}</span>
                                        {customer.contact_position && <span className="text-muted smallest">({customer.contact_position})</span>}
                                    </div>
                                )}
                                {customer.contact_email && (
                                    <a href={`mailto:${customer.contact_email}`} className="d-flex align-items-center gap-2 small text-dark text-decoration-none">
                                        <Mail size={14} className="text-info flex-shrink-0" />
                                        <span>{customer.contact_email}</span>
                                    </a>
                                )}
                                {customer.contact_phone && (
                                    <a href={`tel:${customer.contact_phone}`} className="d-flex align-items-center gap-2 small text-dark text-decoration-none">
                                        <Phone size={14} className="text-success flex-shrink-0" />
                                        <span>{customer.contact_phone}</span>
                                    </a>
                                )}
                                {customer.website && (
                                    <a href={customer.website} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 small text-primary text-decoration-none">
                                        <Globe size={14} className="flex-shrink-0" />
                                        <span>{customer.website}</span>
                                        <ExternalLink size={11} />
                                    </a>
                                )}
                                {customer.meeting_link && (
                                    <a href={customer.meeting_link} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 small text-success text-decoration-none fw-semibold">
                                        <Video size={14} className="flex-shrink-0" />
                                        <span>Google Meet ლინკი</span>
                                        <ExternalLink size={11} />
                                    </a>
                                )}
                            </div>

                            {/* Budget Box */}
                            <div className="mt-3 p-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <div className="text-muted smallest fw-bold text-uppercase mb-1">{isKa ? 'ბიუჯეტი & ღირებულება' : 'Estimated Budget'}</div>
                                <div className="fs-5 fw-bold text-dark font-monospace">
                                    ₾ {parseFloat(customer.estimated_budget || 0).toLocaleString()} {customer.currency}
                                </div>
                            </div>
                        </div>

                        {/* WHAT CLIENT WANTS (კლიენტის მოთხოვნები) */}
                        <div className="shadcn-card p-4 mb-3" style={{ borderLeft: '4px solid #6366f1' }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                    <span>🎯</span> {isKa ? 'რა სურს კლიენტს (Needs & Goals)' : 'What Client Wants'}
                                </h6>
                            </div>
                            {editing ? (
                                <textarea
                                    className="shadcn-input"
                                    rows="4"
                                    value={editForm.client_wants}
                                    onChange={e => setEditForm({ ...editForm, client_wants: e.target.value })}
                                    placeholder={isKa ? 'აღწერეთ კლიენტის მოთხოვნები, პრობლემები...' : 'Describe what client wants...'}
                                />
                            ) : (
                                <p className="small text-dark mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {customer.client_wants || (isKa ? 'მოთხოვნები ჯერ არ არის შეყვანილი' : 'No requirements defined yet')}
                                </p>
                            )}
                        </div>

                        {/* WHAT WE OFFER (ჩვენი შეთავაზება) */}
                        <div className="shadcn-card p-4 mb-3" style={{ borderLeft: '4px solid #059669' }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                    <span>💼</span> {isKa ? 'რას ვთავაზობთ (Orbitronix Solution)' : 'What We Offer'}
                                </h6>
                            </div>
                            {editing ? (
                                <textarea
                                    className="shadcn-input"
                                    rows="4"
                                    value={editForm.our_offer}
                                    onChange={e => setEditForm({ ...editForm, our_offer: e.target.value })}
                                    placeholder={isKa ? 'აღწერეთ ჩვენი შეთავაზებული პაკეტი და გადაწყვეტა...' : 'Describe proposed solution...'}
                                />
                            ) : (
                                <p className="small text-dark mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {customer.our_offer || (isKa ? 'შეთავაზება ჯერ არ არის შეყვანილი' : 'No solution defined yet')}
                                </p>
                            )}
                        </div>

                        {/* AI Autonomous Proposal Generator */}
                        <div className="shadcn-card p-4" style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', border: 'none', color: '#fff' }}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Bot size={22} className="text-warning" />
                                <span className="fw-bold text-white fs-6">AI შეთავაზების გენერატორი</span>
                            </div>
                            <p className="smallest text-white text-opacity-75 mb-3">
                                {isKa 
                                    ? 'AI ავტომატურად ამზადებს ოფიციალურ კომერციულ & ტექნიკურ წერილს კლიენტის მოთხოვნებისა და ბიუჯეტის მიხედვით.' 
                                    : 'AI generates a tailored official proposal based on what client wants and inserts it into the communications timeline.'}
                            </p>
                            <button
                                className="shadcn-btn w-100"
                                style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)', color: '#fff', border: 'none' }}
                                onClick={handleGenerateOffer}
                                disabled={isGeneratingOffer}
                            >
                                {isGeneratingOffer ? (
                                    <><div className="spinner-border spinner-border-sm me-2" />AI მუშაობს...</>
                                ) : (
                                    <><Zap size={16} /> ოფიციალური შეთავაზების გენერირება</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* ── RIGHT COLUMN (8 COLS: TABS) ── */}
                    <div className="col-lg-8">
                        {/* Tab Navigation */}
                        <div className="d-flex gap-1 mb-3 flex-wrap">
                            {TABS.map(tab => (
                                <button
                                    key={tab.key}
                                    className={`shadcn-btn shadcn-btn-sm ${activeTab === tab.key ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.icon}
                                    <span className="ms-1">{tab.label}</span>
                                    {tab.count !== null && tab.count > 0 && (
                                        <span className={`ms-1 badge rounded-pill ${activeTab === tab.key ? 'bg-white text-primary' : 'bg-primary text-white'}`} style={{ fontSize: '10px' }}>
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* ── TAB 1: CHAT & COMMUNICATIONS ── */}
                        {activeTab === 'chat' && (
                            <div className="shadcn-card p-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                        <MessageSquare size={16} className="text-primary" />
                                        {isKa ? 'მიმოწერის & მოლაპარაკებების ისტორია' : 'Communications Timeline'}
                                    </h6>
                                    <span className="text-muted smallest">{communications.length} {isKa ? 'ჩანაწერი' : 'entries'}</span>
                                </div>

                                {/* Messages Stream */}
                                <div 
                                    className="d-flex flex-column gap-3 mb-3 p-3 rounded-3" 
                                    style={{ maxHeight: '520px', overflowY: 'auto', background: '#f8fafc', border: '1px solid #e2e8f0' }}
                                >
                                    {communications.length === 0 ? (
                                        <div className="text-center py-5 text-muted small">
                                            {isKa ? 'კომუნიკაციის ისტორია ცარიელია. დაწერეთ პირველი შეტყობინება ქვემოთ.' : 'No messages yet.'}
                                        </div>
                                    ) : (
                                        communications.map(msg => {
                                            const isAgency = msg.sender_type === 'agency';
                                            const isAi = msg.sender_type === 'autonomous_ai';
                                            // isSys = msg.sender_type === 'system' (unused, style handled by fallback)

                                            return (
                                                <div 
                                                    key={msg.id} 
                                                    className="p-3 rounded-3 shadow-sm position-relative"
                                                    style={{
                                                        background: isAi ? '#f5f3ff' : isAgency ? '#ffffff' : '#f0fdf4',
                                                        border: `1px solid ${isAi ? '#c4b5fd' : isAgency ? '#e2e8f0' : '#bbf7d0'}`,
                                                        borderLeft: `4px solid ${isAi ? '#7c3aed' : isAgency ? '#3b82f6' : '#16a34a'}`
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="fw-bold small text-dark">{msg.sender_name}</span>
                                                            <span className={`badge rounded-pill smallest ${isAi ? 'bg-primary' : isAgency ? 'bg-secondary' : 'bg-success'}`}>
                                                                {isAi ? '🤖 AI Proposal' : isAgency ? 'Agency Staff' : 'Client / Lead'}
                                                            </span>
                                                        </div>
                                                        <span className="smallest text-muted">{new Date(msg.created_at).toLocaleString()}</span>
                                                    </div>
                                                    <div className="small text-dark" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                                        {msg.message}
                                                    </div>
                                                    {msg.attachment_url && (
                                                        <div className="mt-2 pt-2 border-top">
                                                            <a href={msg.attachment_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1">
                                                                <Paperclip size={13} /> {isKa ? 'მიმაგრებული ფაილი' : 'Attachment'}
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={chatEndRef} />
                                </div>

                                {/* Send Input Box */}
                                <div className="d-flex gap-2">
                                    <textarea
                                        className="shadcn-input flex-grow-1"
                                        rows="2"
                                        placeholder={isKa ? 'ჩაწერეთ კომენტარი, შეხვედრის შეჯამება ან შეტყობინება...' : 'Write message or meeting note...'}
                                        value={chatMessage}
                                        onChange={e => setChatMessage(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                    />
                                    <button 
                                        className="shadcn-btn shadcn-btn-primary px-3 d-flex align-items-center justify-content-center"
                                        onClick={handleSendMessage}
                                        disabled={isSending || !chatMessage.trim()}
                                    >
                                        <Send size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── TAB 2: CONTACTS ── */}
                        {activeTab === 'contacts' && (
                            <div className="shadcn-card p-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                        <Users size={16} className="text-primary" />
                                        {isKa ? 'კომპანიის საკონტაქტო პირები & გადაწყვეტილების მიმღებები' : 'Stakeholders & Decision Makers'}
                                    </h6>
                                    <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={() => setShowContactForm(!showContactForm)}>
                                        <Plus size={14} /> {isKa ? 'კონტაქტის დამატება' : 'Add Contact'}
                                    </button>
                                </div>

                                {showContactForm && (
                                    <form onSubmit={handleAddContact} className="p-3 bg-light rounded-3 mb-3 border">
                                        <h6 className="fw-bold small mb-3">{isKa ? 'ახალი საკონტაქტო პირის რეგისტრაცია' : 'Register New Contact'}</h6>
                                        <div className="row g-2 mb-3">
                                            <div className="col-md-6">
                                                <input
                                                    type="text"
                                                    className="shadcn-input"
                                                    placeholder="სახელი *"
                                                    value={contactForm.first_name}
                                                    onChange={e => setContactForm({ ...contactForm, first_name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <input
                                                    type="text"
                                                    className="shadcn-input"
                                                    placeholder="გვარი"
                                                    value={contactForm.last_name}
                                                    onChange={e => setContactForm({ ...contactForm, last_name: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <input
                                                    type="text"
                                                    className="shadcn-input"
                                                    placeholder="თანამდებობა (მაგ: CTO, დირექტორი)"
                                                    value={contactForm.position}
                                                    onChange={e => setContactForm({ ...contactForm, position: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <input
                                                    type="text"
                                                    className="shadcn-input"
                                                    placeholder="ტელეფონი"
                                                    value={contactForm.phone}
                                                    onChange={e => setContactForm({ ...contactForm, phone: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-12">
                                                <input
                                                    type="email"
                                                    className="shadcn-input"
                                                    placeholder="ელ-ფოსტა"
                                                    value={contactForm.email}
                                                    onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-end gap-2">
                                            <button type="button" className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => setShowContactForm(false)}>
                                                {isKa ? 'გაუქმება' : 'Cancel'}
                                            </button>
                                            <button type="submit" className="shadcn-btn shadcn-btn-primary shadcn-btn-sm">
                                                {isKa ? 'შენახვა' : 'Save'}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                <div className="row g-3">
                                    {contacts.length === 0 ? (
                                        <div className="col-12 text-center py-4 text-muted small">
                                            {isKa ? 'დამატებითი კონტაქტები არ არის დამატებული' : 'No additional contacts'}
                                        </div>
                                    ) : (
                                        contacts.map(cnt => (
                                            <div className="col-md-6" key={cnt.id}>
                                                <div className="p-3 rounded-3 border bg-white h-100 d-flex flex-column justify-content-between shadow-sm">
                                                    <div>
                                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                                            <h6 className="fw-bold mb-0 text-dark">{cnt.first_name} {cnt.last_name}</h6>
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-outline-danger p-1 border-0" 
                                                                onClick={() => handleDeleteContact(cnt.id)}
                                                            >
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                        {cnt.position && <div className="smallest text-muted mb-2">{cnt.position}</div>}
                                                        <div className="d-flex flex-column gap-1 smallest text-muted">
                                                            {cnt.phone && (
                                                                <a href={`tel:${cnt.phone}`} className="text-decoration-none text-muted d-flex align-items-center gap-1">
                                                                    <Phone size={12} className="text-success" /> {cnt.phone}
                                                                </a>
                                                            )}
                                                            {cnt.email && (
                                                                <a href={`mailto:${cnt.email}`} className="text-decoration-none text-muted d-flex align-items-center gap-1">
                                                                    <Mail size={12} className="text-info" /> {cnt.email}
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── TAB 3: CALENDAR & EVENTS ── */}
                        {activeTab === 'calendar' && (
                            <div className="shadcn-card p-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                        <Calendar size={16} className="text-primary" />
                                        {isKa ? 'დაგეგმილი შეხვედრები & Follow-up' : 'Scheduled Meetings & Calendar'}
                                    </h6>
                                    <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={() => setShowEventForm(!showEventForm)}>
                                        <Plus size={14} /> {isKa ? 'შეხვედრის დანიშვნა' : 'Schedule Event'}
                                    </button>
                                </div>

                                {showEventForm && (
                                    <div className="p-3 bg-light rounded-3 mb-3 border">
                                        <h6 className="fw-bold small mb-3">{isKa ? 'ახალი შეხვედრის ჩანიშვნა' : 'Schedule Meeting / Event'}</h6>
                                        <div className="row g-2 mb-3">
                                            <div className="col-md-6">
                                                <input
                                                    type="text"
                                                    className="shadcn-input"
                                                    placeholder="სათაური (მაგ: დემო პრეზენტაცია) *"
                                                    value={eventForm.title}
                                                    onChange={e => setEventForm({ ...eventForm, title: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <input
                                                    type="datetime-local"
                                                    className="shadcn-input"
                                                    value={eventForm.event_date}
                                                    onChange={e => setEventForm({ ...eventForm, event_date: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <select
                                                    className="shadcn-input"
                                                    value={eventForm.event_type}
                                                    onChange={e => setEventForm({ ...eventForm, event_type: e.target.value })}
                                                >
                                                    {EVENT_TYPES.map(ev => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <input
                                                    type="url"
                                                    className="shadcn-input"
                                                    placeholder="Google Meet / Zoom ბმული"
                                                    value={eventForm.meeting_link}
                                                    onChange={e => setEventForm({ ...eventForm, meeting_link: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-12">
                                                <textarea
                                                    className="shadcn-input"
                                                    rows="2"
                                                    placeholder="დღის წესრიგი / შენიშვნა..."
                                                    value={eventForm.description}
                                                    onChange={e => setEventForm({ ...eventForm, description: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-end gap-2">
                                            <button type="button" className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => setShowEventForm(false)}>
                                                {isKa ? 'გაუქმება' : 'Cancel'}
                                            </button>
                                            <button type="button" className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={handleAddEvent}>
                                                {isKa ? 'ჩანიშვნა' : 'Schedule'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex flex-column gap-2">
                                    {events.length === 0 ? (
                                        <div className="text-center py-4 text-muted small">
                                            {isKa ? 'დაგეგმილი შეხვედრები არ არის' : 'No scheduled meetings'}
                                        </div>
                                    ) : (
                                        events.map(ev => (
                                            <div key={ev.id} className="p-3 rounded-3 border bg-white d-flex justify-content-between align-items-center shadow-sm">
                                                <div>
                                                    <div className="fw-bold small text-dark d-flex align-items-center gap-2">
                                                        <span>{ev.title}</span>
                                                        <span className="badge bg-light text-dark border smallest">{ev.event_type}</span>
                                                    </div>
                                                    <div className="smallest text-muted mt-1">
                                                        📅 {new Date(ev.event_date).toLocaleString()}
                                                        {ev.description && ` • ${ev.description}`}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    {ev.meeting_link && (
                                                        <a href={ev.meeting_link} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-success d-flex align-items-center gap-1">
                                                            <Video size={13} /> {isKa ? 'შესვლა' : 'Join'}
                                                        </a>
                                                    )}
                                                    <button type="button" className="btn btn-sm btn-outline-danger p-1 border-0" onClick={() => handleDeleteEvent(ev.id)}>
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── TAB 4: FILES & PROPOSALS ── */}
                        {activeTab === 'files' && (
                            <div className="shadcn-card p-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                        <FileText size={16} className="text-primary" />
                                        {isKa ? 'ატვირთული შეთავაზებები & დოკუმენტები' : 'Files, Briefs & Proposals'}
                                    </h6>
                                    <div>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            onChange={handleFileUpload}
                                        />
                                        <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={() => fileInputRef.current.click()}>
                                            <Plus size={14} /> {isKa ? 'ფაილის ატვირთვა' : 'Upload File'}
                                        </button>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    {files.length === 0 ? (
                                        <div className="col-12 text-center py-4 text-muted small">
                                            {isKa ? 'დოკუმენტები არ არის ატვირთული' : 'No documents attached'}
                                        </div>
                                    ) : (
                                        files.map((fl, fIdx) => {
                                            const fileUrl = fl.file_url.startsWith('http') ? fl.file_url : `${API_URL}${fl.file_url}`;
                                            const isPdf = fl.file_type === 'pdf' || fl.file_name.toLowerCase().endsWith('.pdf');
                                            const isImg = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(fl.file_type) || /\.(png|jpe?g|webp|svg)$/i.test(fl.file_name);

                                            return (
                                                <div className="col-md-6" key={fl.id}>
                                                    <div className="p-3 rounded-3 border bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                                                        <div className="d-flex align-items-center gap-3 mb-2">
                                                            <div className="p-2 rounded bg-light text-primary flex-shrink-0">
                                                                {isPdf ? <FileText size={24} className="text-danger" /> : isImg ? <ImageIcon size={24} className="text-info" /> : <FileText size={24} />}
                                                            </div>
                                                            <div className="text-truncate">
                                                                <h6 className="small fw-bold mb-0 text-truncate text-dark" title={fl.file_name}>
                                                                    {fl.file_name}
                                                                </h6>
                                                                <span className="smallest text-muted">
                                                                    {new Date(fl.created_at).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex gap-2 pt-2 border-top">
                                                            {(isPdf || isImg) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                                                    onClick={() => {
                                                                        if (isPdf) {
                                                                            setActivePdfModal({ title: fl.file_name, file_url: fileUrl });
                                                                        } else if (isImg) {
                                                                            const imgFiles = files.filter(f => ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(f.file_type) || /\.(png|jpe?g|webp|svg)$/i.test(f.file_name));
                                                                            const mapped = imgFiles.map(img => ({
                                                                                url: img.file_url.startsWith('http') ? img.file_url : `${API_URL}${img.file_url}`,
                                                                                title: img.file_name
                                                                            }));
                                                                            const curIdx = Math.max(0, imgFiles.findIndex(f => f.id === fl.id));
                                                                            openLightbox(mapped, curIdx);
                                                                        }
                                                                    }}
                                                                >
                                                                    <Eye size={13} /> {isKa ? 'ნახვა' : 'Preview'}
                                                                </button>
                                                            )}
                                                            <a href={fileUrl} target="_blank" rel="noreferrer" download className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 flex-grow-1 justify-content-center">
                                                                <Download size={13} /> {isKa ? 'ჩამოტვირთვა' : 'Download'}
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── TAB 5: DETAILS & NOTES ── */}
                        {activeTab === 'details' && (
                            <div className="shadcn-card p-4">
                                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                    <Edit3 size={16} className="text-primary" />
                                    {isKa ? 'კლიენტის სრული მონაცემები & პარამეტრები' : 'Full Customer Specifications'}
                                </h6>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'კომპანიის სახელი' : 'Company Name'}</label>
                                        <input
                                            type="text"
                                            className="shadcn-input"
                                            value={editForm.name}
                                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'საორიენტაციო ბიუჯეტი (₾)' : 'Budget (GEL)'}</label>
                                        <input
                                            type="number"
                                            className="shadcn-input"
                                            value={editForm.estimated_budget}
                                            onChange={e => setEditForm({ ...editForm, estimated_budget: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'საკონტაქტო პირი' : 'Contact Person'}</label>
                                        <input
                                            type="text"
                                            className="shadcn-input"
                                            value={editForm.contact_person}
                                            onChange={e => setEditForm({ ...editForm, contact_person: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'თანამდებობა' : 'Position'}</label>
                                        <input
                                            type="text"
                                            className="shadcn-input"
                                            value={editForm.contact_position}
                                            onChange={e => setEditForm({ ...editForm, contact_position: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'ელ-ფოსტა' : 'Email'}</label>
                                        <input
                                            type="email"
                                            className="shadcn-input"
                                            value={editForm.contact_email}
                                            onChange={e => setEditForm({ ...editForm, contact_email: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'ტელეფონი' : 'Phone'}</label>
                                        <input
                                            type="text"
                                            className="shadcn-input"
                                            value={editForm.contact_phone}
                                            onChange={e => setEditForm({ ...editForm, contact_phone: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'ვებსაიტი' : 'Website'}</label>
                                        <input
                                            type="url"
                                            className="shadcn-input"
                                            value={editForm.website}
                                            onChange={e => setEditForm({ ...editForm, website: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold">{isKa ? 'Google Meet ბმული' : 'Google Meet Link'}</label>
                                        <input
                                            type="url"
                                            className="shadcn-input"
                                            value={editForm.meeting_link}
                                            onChange={e => setEditForm({ ...editForm, meeting_link: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label small fw-bold">{isKa ? 'შიდა შენიშვნები' : 'Internal Notes'}</label>
                                        <textarea
                                            className="shadcn-input"
                                            rows="3"
                                            value={editForm.notes}
                                            onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                                            disabled={!editing}
                                        />
                                    </div>
                                </div>
                                {editing && (
                                    <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                                        <button className="shadcn-btn shadcn-btn-secondary" onClick={() => setEditing(false)}>
                                            {isKa ? 'გაუქმება' : 'Cancel'}
                                        </button>
                                        <button className="shadcn-btn shadcn-btn-primary" onClick={handleSave} disabled={saving}>
                                            <Save size={15} /> {saving ? (isKa ? 'ინახება...' : 'Saving...') : (isKa ? 'შენახვა' : 'Save Changes')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── PDF PREVIEW POPUP MODAL ── */}
            {activePdfModal && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    onClick={() => setActivePdfModal(null)}
                    style={{ background: 'rgba(5, 8, 22, 0.88)', backdropFilter: 'blur(8px)', zIndex: 1060 }}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered modal-xl" 
                        style={{ maxWidth: '1100px', width: '95%' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-content border-0 shadow-lg text-white" style={{ borderRadius: '16px', overflow: 'hidden', background: '#0f172a' }}>
                            <div className="modal-header border-bottom border-secondary border-opacity-25 p-3 px-4 d-flex justify-content-between align-items-center" style={{ background: '#0b1120' }}>
                                <div className="d-flex align-items-center gap-3 text-truncate me-3">
                                    <FileText size={22} className="text-danger flex-shrink-0" />
                                    <h6 className="modal-title fw-bold text-white mb-0 text-truncate">{activePdfModal.title}</h6>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <a 
                                        href={activePdfModal.file_url} 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                                    >
                                        <ExternalLink size={14} /> {isKa ? 'გახსნა' : 'Open'}
                                    </a>
                                    <a 
                                        href={activePdfModal.file_url} 
                                        download 
                                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                    >
                                        <Download size={14} /> {isKa ? 'ჩამოტვირთვა' : 'Download'}
                                    </a>
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-light rounded-circle p-2 ms-2 d-flex align-items-center justify-content-center"
                                        style={{ width: '34px', height: '34px' }}
                                        onClick={() => setActivePdfModal(null)}
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                            <div className="modal-body p-0" style={{ height: '78vh', background: '#1e293b' }}>
                                <iframe 
                                    src={activePdfModal.file_url}
                                    title={activePdfModal.title}
                                    width="100%" 
                                    height="100%" 
                                    style={{ border: 'none', display: 'block' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── IMAGE LIGHTBOX ── */}
            {isLightboxOpen && lightboxImages.length > 0 && (
                <div 
                    className="orbx-lightbox-overlay" 
                    onClick={closeLightbox}
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(5, 8, 22, 0.94)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 999999,
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center',
                        padding: '24px'
                    }}
                >
                    <div className="d-flex justify-content-between align-items-center w-100" style={{ maxWidth: '1200px' }} onClick={e => e.stopPropagation()}>
                        <span className="fw-bold fs-6 text-white text-truncate">{lightboxImages[lightboxIndex]?.title}</span>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge rounded-pill bg-white bg-opacity-10 text-white px-3 py-2 fw-semibold">
                                {lightboxIndex + 1} / {lightboxImages.length}
                            </span>
                            <button type="button" onClick={closeLightbox} className="btn btn-outline-light rounded-circle p-2" style={{ width: '40px', height: '40px' }}>
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="position-relative d-flex align-items-center justify-content-center flex-grow-1 w-100 my-3" onClick={e => e.stopPropagation()}>
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={prevLightboxImage}
                                className="btn position-absolute start-0 top-50 translate-middle-y text-white rounded-circle shadow-lg"
                                style={{ zIndex: 1000001, width: '54px', height: '54px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}
                            >
                                <ChevronLeft size={30} />
                            </button>
                        )}
                        <img
                            src={lightboxImages[lightboxIndex]?.url}
                            alt=""
                            style={{ maxHeight: '78vh', maxWidth: '82vw', objectFit: 'contain', borderRadius: '12px' }}
                        />
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={nextLightboxImage}
                                className="btn position-absolute end-0 top-50 translate-middle-y text-white rounded-circle shadow-lg"
                                style={{ zIndex: 1000001, width: '54px', height: '54px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}
                            >
                                <ChevronRight size={30} />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminPotentialCustomerDetail;
